import { Component, HostBinding, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import * as fromRoot from '../../reducers';
import * as AuthActions from '../../actions/auth.actions';
import { AuthUserPassword, BlendEvent, ErrCode } from "src/app/shared/models";
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ShareInviteService } from "src/app/shares/share-invite.service";
import { FeatureService } from "src/app/shared/services/feature.service";
import { LoggerService } from "src/app/core/logger.service";
import { UserService } from 'src/app/core/user.service';
import { StoreClearAction } from "src/app/actions/core.actions";
import { BlendService } from "src/app/shared/services/blend.service";
import { CookieService } from "ngx-cookie-service";
import { SyncCryptService } from "src/app/core/crypt/sync-crypt.service";
import { Base64Service } from "src/app/core/base64.service";
import { ChargebeeService } from "src/app/shared/services/chargebee.service";

interface Fields {
  emailAddress: string;
  password: string;
}

type Field = keyof Fields;

type FormControls<T> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  protected twofacode: string;
  private subscription: Subscription;
  protected errCode;
  protected spinner: boolean = false;
  protected twofaRequired: boolean;
  protected ctx: string;
  protected form: FormGroup<FormControls<Fields>>;
  // When joining a share, these fields are used.
  private cachekey: string;
  private wrapKey: string;
  public shareLabel: string;
  public shareFolderName: string;

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private shareInviteService: ShareInviteService,
    private featureService: FeatureService,
    private loggerService: LoggerService,
    private userService: UserService,
    private blendService: BlendService,
    private cookies: CookieService,
    private crypt: SyncCryptService,
    private Base64: Base64Service,
    private chargebeeService: ChargebeeService
  ) {}

  async ngOnInit() {
    this.store.dispatch(new StoreClearAction());
    this.store.dispatch(new AuthActions.LogoutAction());
    this.form = this.fb.group({
      emailAddress: ['', Validators.compose([
        Validators.required,
        Validators.email,
      ])],
      password: ['', Validators.required],
    });

    this.subscription = this.store
      .select(fromRoot.getAuthState)
      .subscribe((data) => {
          if (data && !data.authenticated && data.error) {
              // this.loginProgress = 0;
              this.spinner = false;
              // this.log.e('Error log in ', data.error);
              switch (data.error.code) {
                  case 8050:
                  case 8051:
                      // suspended
                      this.router.navigate(['/account-suspended'], { queryParams: data.error.data });
                      break;
                  case 8054:
                      // incomplete
                      this.router.navigate(['/account-suspended'], { queryParams: { ...data.error.data, m: 2 } });
                      break;
                  case 8055:
                      // migrating
                      this.router.navigate(['/account-suspended'], { queryParams:  { ...data.error.data, m: 1 }});
                      break;
                  case 8052:
                      this.router.navigate(['/accounting-hold'], { queryParams: data.error.data});
                      break;
                  case 9429:
                      this.errCode = data.error;
                      // this.loginProgress = 0;
                      // this.spinner = false;
                      this.twofaRequired = false;
                      this.password = '';
                      this.twofacode = '';
                      break;
                  case 8022:
                      // this.spinner = false;
                      this.twofaRequired = true;
                      // this.loginProgress = 0;
                      // this.loginForm.get('twofacode').setValue('');
                      this.twofacode = '';
                      break;
                  default:
                      this.errCode = new ErrCode(6001);
                      // this.loginProgress = 0;
                      // this.spinner = false;
                      this.twofaRequired = false;
                      // this.loginForm.get('twofacode').setValue('');
                      // this.loginForm.get('password').setValue('');
                      this.password = '';
                      this.twofacode = '';

                      break;
              }
              // reset the auth state, update the validity of the form
              // and then early exit the function
              this.store.dispatch(new AuthActions.LogoutAction());
              // this.loginForm.updateValueAndValidity();
              return;
          }

          if (data.twofarequired && !this.twofaRequired) {
              this.spinner = false;
              this.twofaRequired = true;
              // this.loginProgress = 0;
          }
          // if (data.progress > this.loginProgress) {
          //     this.loginProgress = data.progress;
          //     // the current bCrypt lib we use stops at
          //     // 85 for progress - set the progress to 100 instead.
          //     if (data.authenticated || data.progress >= 85) {
          //         this.loginProgress = 100;
          //     }
          // }

          if (data.authenticated && this.ctx != 'share') {
              this.handleLogin();
              // this.spinner = false;
          } else if (data.authenticated && this.ctx == 'share') {
              this.handleShareLogin();
              this.spinner = false;
          }
      });    
    switch (this.route.snapshot.queryParams['ctx']) {
      case 'share':
        this.cachekey = this.route.snapshot.queryParams['cachekey'];
        this.wrapKey = this.route.snapshot.fragment;
        this.handleShare();
    }

    this.featureService.resetFeatureFlags();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  protected get page(): 'signin' | 'twofa' {
    if (this.twofaRequired) {
      return 'twofa';
    }
    else {
      return 'signin';
    }
  }

  onResetToSignin() {
    this.twofaRequired = false;
  }

    private async handleLogin() {
        const sel = this.store.select(fromRoot.getSignApiData);
        const sub = sel.subscribe(async (data) => {
            const values = {
                access_token: data.access_token,
                access_secret: await this.crypt.storeDecrypt(
                    'access_secret'
                ),
                uid: data.uid,
                web_device_id: data.web_device_id,
            };
            const token = this.Base64.encode(Object.values(values).join('|'));
            const expDate = new Date();
            expDate.setMonth(expDate.getMonth() + 3);

            this.cookies.set(
                'sync_auth',
                token,
                expDate,
                '/',
                environment.cookiedomain,
                environment.cookiesecure,
                'Lax'
            );
            sub.unsubscribe();
        });
        this.chargebeeService.refreshSubscription();

        this.blendService.track(BlendEvent.SIGN_IN);
        if (this.route.snapshot.queryParams['loc']) {
            // this.Dispatch.run();
        } else if (this.route.snapshot.queryParams['return_to']) {
            const path = decodeURIComponent(this.route.snapshot.queryParams['return_to']);
            window.location.replace(
                path.substring(0, 1) == '/' ? path : `/${path}`
            );
        } else if (this.route.snapshot.queryParams['return_to_link']) {
            const path = decodeURIComponent(this.route.snapshot.queryParams['return_to_link']);
            const linkPath = this.route.snapshot.fragment
                ? `${environment.linkhost}${path}#${this.route.snapshot.fragment}`
                : `${environment.linkhost}${path}`;
            window.location.replace(linkPath);
        } else {
            const dashboard = this.userService.get('dashboard') || 'files';
            this.router.navigate([`/${dashboard}`], { queryParams: undefined });
            return;
        }
    }

  private async handleShareLogin() {
    const inviteData = await this.shareInviteService.getInvite(
        this.cachekey,
        this.wrapKey
    );
    await this.shareInviteService.accept(this.wrapKey, 0, inviteData);
    this.router.navigate(['/shares'], { queryParams: undefined });
  }

  public async handleShare() {
    if (!this.route.snapshot.queryParams['cachekey']) {
        this.loggerService.error('Missing cachekey, cannot continue');
        this.ctx = undefined;
        return;
    }
    if (!this.wrapKey) {
        this.loggerService.error('Missing wrap key, cannot continue');
        this.ctx = undefined;
        return;
    }

    try {
        const data = await this.shareInviteService.getInvitePublic(
            this.route.snapshot.queryParams['cachekey'],
            this.wrapKey
        );
        this.shareFolderName = data.name;
        this.shareLabel = data.label;
    } catch (err) {
        if (typeof err == 'object' && err.code) {
            if (err.code == 1620) {
                this.wrapKey = '';
                this.ctx = undefined;
            }
            this.errCode = err;
        } else if (typeof err == 'object' && err.error_code == 1600) {
            this.ctx = undefined;
            this.errCode = ErrCode.fromApiLegacy(err);
        } else if (typeof err == 'object' && err.error_code) {
            this.errCode = ErrCode.fromApiLegacy(err);
        } else {
            this.errCode = new ErrCode(
                1000,
                'An error retrieving share data occurred'
            );
        }
    }
}

  protected getErrorMessage(field: Field) {
    if (!this.form.controls[field].touched) return;

    if (field == 'emailAddress') {
      if (this.form.controls.emailAddress.hasError('required')) {
        return 'Please enter an email address';
      }
      else if (this.form.controls.emailAddress.hasError('email')) {
        return 'Please enter a valid email address';
      }
    }
    else if (field == 'password') {
      if (this.form.controls.password.hasError('required')) {
        return 'Please enter a password';
      }
    }
  }

  protected get showVerifiedMessage() {
    return this.route.snapshot.queryParams['verified'];
  }

  protected get showPasswordResetMessage() {
    return this.route.snapshot.queryParams['passwordreset'];
  }

  protected get showPasswordSetMessage() {
    return this.route.snapshot.queryParams['passwordset'];
  }

  private get emailAddress() {
    return this.form.controls.emailAddress.value?.trim();
  }

  private set emailAddress(value: string) {
    this.form.controls.emailAddress.setValue(value?.trim());
  }

  onEmailBlur = () => {
    const emailControl = this.form.controls.emailAddress;
    emailControl.setValue(emailControl.value?.trim());
  }
  
  private get password() {
    return this.form.controls.password.value;
  }

  private set password(value: string) {
    this.form.controls.password.setValue(value);
  }

  // UI handlers
  protected onClickForgotPassword = () => {
    this.router.navigateByUrl('/forgot-password');
  }

  protected onClickBackToSignIn = () => {
    this.router.navigateByUrl('/login');
  }

  protected onSubmit = () => {
    this.spinner = true;
    const item = new AuthUserPassword(
      this.emailAddress,
      this.password,
      this.twofacode,
    );
    this.store.dispatch(new AuthActions.LoginAction(item));
  }

  protected handleTwoFactor(value) {
    this.twofacode = value;
    this.onSubmit();
  }

  protected onClickCreateAccount = () => {
    window.document.location.href = 'https://www.sync.com/pricing';
  }
}